/**
 * Ask-User (HITL) Showcase (dev only)
 *
 * Renders `InlineAskUserCard` across every question type and state in its
 * composer-attached form (with a mock composer below each), using mocked
 * answer/cancel handlers so the visuals can be iterated without a backend.
 *
 * Visit `/dev/ask-user-showcase` in dev mode.
 */

import { useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  type ChatModelAdapter,
} from '@assistant-ui/react';
import { ToastProvider } from '@/components/ui';
import {
  InlineAskUserCard,
  type InlineAskUserArgs,
} from '@/components/assistant-ui/inline-ask-user-card';
import { AskUserSummary } from '@/components/assistant-ui/ask-user-summary';
import { StructuredAnswerBlock } from '@/components/assistant-ui/StructuredAnswerBlock';
import type { AskUserQuestion, AskUserAnswer } from '@/types/inbox';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false }, mutations: { retry: false } },
});

/**
 * `InlineAskUserCard` calls `useAssistantRuntime({ optional: true })`, but in
 * @assistant-ui v0.12 that hook dereferences the assistant client before the
 * `optional` check — so it throws "requires an AuiProvider" when rendered
 * outside a runtime. The real app always hosts the card inside a Thread
 * runtime; this preview supplies a no-op local runtime so the same is true here.
 * Answering still goes through the mocked override handlers, never this runtime.
 */
const noopAdapter: ChatModelAdapter = {
  async run() {
    return { content: [] };
  },
};

function PreviewRuntime({ children }: { children: React.ReactNode }) {
  const adapter = useMemo(() => noopAdapter, []);
  const runtime = useLocalRuntime(adapter);
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}

const noop = async () => {
  /* preview: resolve immediately so the card flips to its answered/cancelled state */
};

interface Scenario {
  title: string;
  args: InlineAskUserArgs;
}

const SCENARIOS: Scenario[] = [
  {
    title: 'Free text',
    args: {
      askUserRequestId: 'aur-text',
      questions: [
        {
          questionId: 'q1',
          question: 'What should the report focus on?',
          inputType: 'text',
        },
      ],
    },
  },
  {
    title: 'Single select (with Other + comment)',
    args: {
      askUserRequestId: 'aur-select',
      questions: [
        {
          questionId: 'q1',
          question: 'Which would you like to do next?',
          inputType: 'select',
          allowOther: true,
          allowComment: true,
          options: [
            { label: 'Option A', description: 'Ship the current draft' },
            { label: 'Option B', description: 'Iterate once more' },
            { label: 'Option C' },
          ],
        },
      ],
    },
  },
  {
    title: 'Multi select',
    args: {
      askUserRequestId: 'aur-multi',
      questions: [
        {
          questionId: 'q1',
          question: 'Which of these would you like to include? (select all that apply)',
          inputType: 'multi_select',
          allowOther: true,
          options: [
            { label: 'Option A' },
            { label: 'Option B' },
            { label: 'Option C' },
            { label: 'Option D' },
          ],
        },
      ],
    },
  },
  {
    title: 'Confirm (Yes / No)',
    args: {
      askUserRequestId: 'aur-confirm',
      questions: [
        {
          questionId: 'q1',
          question: 'Proceed with deploying to **production**?',
          inputType: 'confirm',
        },
      ],
    },
  },
  {
    title: 'Review (approve / dismiss)',
    args: {
      askUserRequestId: 'aur-review',
      questions: [
        {
          questionId: 'q1',
          question:
            'Please review the **AI-first product development** plan before I start.',
          inputType: 'review',
          surfaceRef: { surfaceName: 'Plan' },
        },
      ],
    },
  },
  {
    title: 'Review — long content',
    args: {
      askUserRequestId: 'aur-review-long',
      questions: [
        {
          questionId: 'q1',
          inputType: 'review',
          surfaceRef: { surfaceName: 'SUP-1042' },
          question: [
            '## SUP-1042: cancelled call, twice',
            '',
            '**Summary:** An agent received 2 calls in a single availability window in the contact-center app that were both cancelled, causing a missed callback request. Incident occurred during the documented ring-failure cluster.',
            '',
            '**Root Cause:**',
            '- Caller-initiated disconnections during the 2-5 second reservation ring phase (expected behavior)',
            '- Possible system-initiated cancellations from the ring-failure cluster affecting 16+ agents',
            '- This is the 9th+ identical ticket with this pattern in 2 days',
            '',
            '**Proposed Fix:**',
            '1. Close ticket as "Won\'t Do" / "Working as Designed" — no system defect',
            '2. Inform reporter that cancelled calls are caused by caller hangup or the known ring issue, not agent error',
            '3. Link to related tickets (SUP-1037, SUP-1031, SUP-1024)',
            '4. Recommend team-wide communication about cancellation behavior to reduce future ticket volume',
            '',
            '**Risk:** None - No system changes proposed. Informational resolution only.',
            '',
            '> Full analysis and resolution details available in the conversation canvases and the Support Requests knowledge base.',
          ].join('\n'),
        },
      ],
    },
  },
  {
    title: 'Multiple questions (tabs)',
    args: {
      askUserRequestId: 'aur-multiq',
      questions: [
        {
          questionId: 'q1',
          header: 'Scope',
          question: 'How broad should the research be?',
          inputType: 'select',
          options: [{ label: 'Narrow' }, { label: 'Broad' }],
        },
        {
          questionId: 'q2',
          header: 'Tone',
          question: 'What tone should the summary use?',
          inputType: 'text',
        },
      ],
    },
  },
  {
    title: 'Answered (resolved)',
    args: {
      askUserRequestId: 'aur-answered',
      status: 'answered',
      questions: [
        { questionId: 'q1', question: 'Already answered.', inputType: 'text' },
      ],
    },
  },
];

// ─── Thread-rendered turns (summary + answer block) ─────────────────────────
//
// `AskUserSummary` (assistant turn) and `StructuredAnswerBlock` (user turn) read
// plain props, so they render here without a runtime/message context.

const SELECT_QUESTIONS: AskUserQuestion[] = [
  {
    questionId: 'q1',
    header: 'Next step',
    question: 'What should I do next with notes/sample.md?',
    inputType: 'select',
    options: [
      { label: 'Show Tea as a diff', description: 'Render the Tea section as a green/added diff in a new canvas, like we did before.' },
      { label: 'Add steeping tips', description: 'Expand the Tea section with brewing tips and origins for each variety.' },
      { label: 'Coffee deep-dive', description: 'Add a detailed coffee section with brew methods, ratios, and tasting notes.' },
      { label: 'Tidy up the VFS', description: "Review what's stored and clean up or reorganize files in the conversation VFS." },
    ],
  },
];

const MULTI_QUESTIONS: AskUserQuestion[] = [
  { questionId: 'q1', header: 'Scope', question: 'How broad should the research be?', inputType: 'select', options: [{ label: 'Narrow', description: 'Just the headline finding.' }, { label: 'Broad', description: 'Cover adjacent topics too.' }] },
  { questionId: 'q2', header: 'Tone', question: 'What tone should the summary use?', inputType: 'text' },
];

const SELECT_ANSWER: AskUserAnswer[] = [
  { questionId: 'q1', value: 'Coffee deep-dive', comment: 'Focus on pour-over.' },
];

const MULTI_ANSWER: AskUserAnswer[] = [
  { questionId: 'q1', value: 'Broad' },
  { questionId: 'q2', value: 'Friendly and concise' },
];

export default function AskUserShowcasePage() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <PreviewRuntime>
        <div className="mx-auto max-w-2xl px-6 py-10">
          <h1 className="mb-1 text-xl font-semibold text-foreground">
            Ask-User (HITL) — thread turns
          </h1>
          <p className="mb-6 text-sm text-muted-foreground">
            The assistant question summary and the structured answer block as
            they render inline in the conversation thread.
          </p>
          <div className="mb-12 space-y-6">
            <section>
              <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Assistant turn — single question summary
              </h2>
              <AskUserSummary questions={SELECT_QUESTIONS} />
            </section>
            <section>
              <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Assistant turn — multi-question summary
              </h2>
              <AskUserSummary questions={MULTI_QUESTIONS} />
            </section>
            <section>
              <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                User turn — answer block (select + comment)
              </h2>
              <StructuredAnswerBlock questions={SELECT_QUESTIONS} answers={SELECT_ANSWER} />
            </section>
            <section>
              <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                User turn — answer block (multi-question)
              </h2>
              <StructuredAnswerBlock questions={MULTI_QUESTIONS} answers={MULTI_ANSWER} />
            </section>
          </div>
          <h1 className="mb-1 text-xl font-semibold text-foreground">
            Ask-User (HITL) — composer-attached card
          </h1>
          <p className="mb-8 text-sm text-muted-foreground">
            Every question type and state, rendered as the card shows attached
            above the message input. Answer/dismiss are mocked — actions flip the
            card to its resolved state without a backend.
          </p>

          <div className="space-y-8">
            {SCENARIOS.map((s) => (
              <section key={s.args.askUserRequestId}>
                <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {s.title}
                </h2>
                <InlineAskUserCard
                  args={s.args}
                  attached
                  onAnswerOverride={noop}
                  onCancelOverride={noop}
                />
                {/* Mock composer (mirrors the real rounded-3xl input). */}
                <div className="rounded-3xl border border-border bg-muted px-4 py-3 text-sm text-muted-foreground shadow-2xl shadow-black/40">
                  How can I help you today?
                </div>
              </section>
            ))}
          </div>
        </div>
        </PreviewRuntime>
      </ToastProvider>
    </QueryClientProvider>
  );
}
